import {
  Badge,
  Box,
  Icon,
  type Omittable,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
} from "@salestrak/ui";
import type React from "react";

/**
 * System markers — the marks annotating an entity with a fact the SYSTEM owns
 * rather than a value a user set: which type new projects get, which workflow
 * a workspace falls back to, which types SalesTrak ships.
 *
 * One mark serves every surface (a table's name column, a preview header, a
 * detail dialog, a settings masthead, a settings row): the marker means the
 * same thing on all of them, so it has to be recognizable at a glance on all
 * of them.
 *
 * Two forms:
 *
 *   - `builtIn` is a bare filled lock. Shipped-by-SalesTrak is a constraint
 *     (it cannot be renamed or deleted), and the lock is the constraint glyph
 *     everywhere else in the app (`AccessMarker`). It carries no word: the
 *     glyph is the whole statement, and the tooltip names who built it.
 *   - `default` is a small chip in the theme accent, because "which one new
 *     things get" has no glyph and needs its one word. It is the same
 *     soft-primary chip that marks a primary contact: the system's pick,
 *     stated in the theme's own accent rather than a status hue (green means
 *     *healthy* everywhere else here). It wears the 18px `default` rung, not
 *     the 20px value chip: a marker annotates the row's value, it is not one,
 *     so it sits a step below the name. No `pill`: the capsule is the tag
 *     family's signature (see `badge.tsx` `pill`), and a marker wearing it
 *     would read as data riding the row.
 *
 * The label stays terse and the MEANING rides the tooltip, keyed by subject:
 * what being the default DOES differs per noun, and an 18px chip has no room
 * to say it.
 */

/** The nouns a `default` marker annotates, minus the one whose noun is not
 *  fixed. Each one's hint states what being the default actually does. */
export type DefaultMarkerSubject = "workflow" | "issueType" | "stage";

const DEFAULT_HINT: Record<DefaultMarkerSubject, string> = {
  issueType: "New Projects use this type",
  stage: "New Projects land in this stage",
  workflow: "New Projects use this workflow",
};

const BUILT_IN_HINT = "Built by SalesTrak";

/**
 * The default designation as a WORD on a settings row's meta line ("5
 * projects · Default · description"), for lists whose rows already speak
 * in dot-joined meta. The chip stays for mastheads, previews and detail
 * dialogs, where the marker rides the name and its hint rides the hover.
 */
export const DEFAULT_META_LABEL = "Default";

/**
 * The default marker's hint text, for the one surface that cannot host the
 * chip: the stages dialog's stage pill, whose label swaps to an input in
 * place and whose meta reads as a dot-joined run beside it. It states the
 * same fact, so it reads from the same copy.
 */
export function defaultMarkerHint(subject: DefaultMarkerSubject): string {
  return DEFAULT_HINT[subject];
}

export type SystemMarkerProps = Omittable<{
  /** Where the hint opens. `bottom` for a masthead adornment, which has page
   *  chrome above it. */
  side?: React.ComponentProps<typeof TooltipPopup>["side"];
}> &
  (
    | { kind: "builtIn" }
    | { kind: "default"; subject: DefaultMarkerSubject }
    /** The account/contact type lists, whose noun is the tab's
     *  (`KIND_LABEL[entityKind]`) rather than a fixed word. */
    | { kind: "default"; subject: "localType"; noun: string }
  );

export function SystemMarker(props: SystemMarkerProps): React.ReactElement {
  const { label, hint } = markerCopy(props);
  return (
    <Tooltip>
      {/* A marker is not a control, so the trigger is a plain wrapper rather
          than TooltipTrigger's default button — that would be a tab stop
          that does nothing. */}
      <TooltipTrigger
        render={<Box direction="row" align="center" shrink={false} />}
      >
        {props.kind === "builtIn" ? (
          // The glyph is the only thing on screen, so it carries the marker's
          // name for readers who cannot see it; the tooltip carries the fact.
          <Icon aria-label={label} color="faint" name="lock-filled" size="xs" />
        ) : (
          <Badge color="primary" tone="soft">
            {label}
          </Badge>
        )}
      </TooltipTrigger>
      <TooltipPopup side={props.side ?? "top"}>{hint}</TooltipPopup>
    </Tooltip>
  );
}

function markerCopy(props: SystemMarkerProps): {
  label: string;
  hint: string;
} {
  if (props.kind === "builtIn") {
    return { hint: BUILT_IN_HINT, label: "System" };
  }
  return {
    hint:
      props.subject === "localType"
        ? `New ${props.noun}s use this type`
        : DEFAULT_HINT[props.subject],
    label: DEFAULT_META_LABEL,
  };
}
